import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Download, Mail, MessageCircle, Loader2, RefreshCw, ScrollText, ExternalLink } from 'lucide-react';
import { API, adminAuthHeader } from '../api';

/**
 * In-app voucher preview modal. Fetches the voucher PDF as a Blob and renders
 * it inside an <iframe>. Also exposes Download / Email / WhatsApp actions so
 * the admin never has to leave this modal to send the voucher.
 */
const VoucherPreviewModal = ({ it, open, onClose, onDownload, onEmail, onWhatsApp }) => {
  const [blobUrl, setBlobUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [voucherNo, setVoucherNo] = useState('');

  useEffect(() => {
    if (!open || !it?.id) return;
    let cancelled = false;
    let currentUrl = '';
    const load = async () => {
      setLoading(true); setErr('');
      try {
        const res = await axios.get(`${API}/admin/itineraries/${it.id}/voucher-pdf`, {
          headers: adminAuthHeader(),
          responseType: 'blob',
        });
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setBlobUrl(currentUrl);
        // The freshly-assigned voucher_no comes back in the Content-Disposition
        // filename (e.g. Voucher-TS-VCH-000001.pdf). Parse it so the modal can
        // show the assigned number without a second API roundtrip.
        const cd = res.headers['content-disposition'] || '';
        const m = cd.match(/TS-VCH-\d+/);
        if (m) setVoucherNo(m[0]);
      } catch (e) {
        if (!cancelled) setErr('Could not load voucher. Please retry.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [open, it?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const bookingId = it ? `TS-${String(it.id || '').slice(0, 8).toUpperCase()}` : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 md:p-6" data-testid="voucher-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-gray-100 bg-gradient-to-r from-[#0d64a8] to-[#083f6d] text-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-md bg-white/15 border border-white/30 flex items-center justify-center shrink-0">
              <ScrollText size={16} className="text-[#ffcc29]" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#ffcc29] font-semibold">Trip Voucher Preview</div>
              <div className="text-sm md:text-base font-semibold truncate">{it?.trip_title || 'Untitled trip'}</div>
              <div className="text-[11px] text-white/70 flex items-center gap-2 flex-wrap">
                {voucherNo && <span className="font-mono">{voucherNo}</span>}
                {voucherNo && bookingId && <span className="opacity-50">·</span>}
                {bookingId && <span className="font-mono opacity-80">Booking {bookingId}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onDownload(it)}
              className="hidden md:inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/25 text-white text-xs px-3 py-1.5 rounded-md"
              data-testid="voucher-modal-download"
            >
              <Download size={13} /> Download
            </button>
            <button
              onClick={() => onEmail(it)}
              className="hidden md:inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/25 text-white text-xs px-3 py-1.5 rounded-md"
              data-testid="voucher-modal-email"
            >
              <Mail size={13} /> Email
            </button>
            <button
              onClick={() => onWhatsApp(it)}
              className="hidden md:inline-flex items-center gap-1.5 bg-[#25D366]/90 hover:bg-[#25D366] border border-[#25D366] text-white text-xs px-3 py-1.5 rounded-md"
              data-testid="voucher-modal-whatsapp"
            >
              <MessageCircle size={13} /> WhatsApp
            </button>
            {blobUrl && (
              <a
                href={blobUrl}
                target="_blank"
                rel="noreferrer"
                className="hidden md:inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/25 text-white text-xs px-3 py-1.5 rounded-md"
                title="Open in new tab"
              >
                <ExternalLink size={13} />
              </a>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-white/10 hover:bg-white/20 border border-white/25 text-white"
              data-testid="voucher-modal-close"
              title="Close (Esc)"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body: PDF iframe */}
        <div className="flex-1 min-h-0 bg-gray-100 relative">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-500 text-sm">
              <Loader2 className="animate-spin" size={22} />
              <span>Generating voucher PDF…</span>
            </div>
          )}
          {err && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-red-500 text-sm">
              <span>{err}</span>
              <button
                onClick={() => { setBlobUrl(''); setErr(''); setTimeout(() => setBlobUrl('force'), 0); }}
                className="mt-2 inline-flex items-center gap-1 bg-red-500 text-white text-xs px-3 py-1.5 rounded-md hover:bg-red-600"
              >
                <RefreshCw size={12} /> Retry
              </button>
            </div>
          )}
          {blobUrl && !loading && !err && (
            <object
              data={blobUrl}
              type="application/pdf"
              className="w-full h-full"
              data-testid="voucher-modal-iframe"
              aria-label="Trip Voucher PDF"
            >
              {/* Fallback for browsers that block inline PDF (e.g. some mobile or corp policies) */}
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-white p-6 text-center">
                <ScrollText size={32} className="text-[#ffcc29]" />
                <div className="text-sm text-gray-700 font-medium">Inline PDF preview isn't supported by this browser.</div>
                <div className="text-xs text-gray-500 max-w-md">The voucher was generated successfully. Use the buttons below to download, email or share it.</div>
                <a
                  href={blobUrl}
                  download={`Voucher-${voucherNo || 'trip'}.pdf`}
                  className="mt-2 inline-flex items-center gap-1.5 bg-[#0d64a8] text-white text-xs px-4 py-2 rounded-md hover:bg-[#0a548e]"
                >
                  <Download size={13} /> Open the PDF
                </a>
              </div>
            </object>
          )}
        </div>

        {/* Mobile action bar (compact) */}
        <div className="md:hidden border-t border-gray-100 grid grid-cols-3 gap-2 p-3">
          <button onClick={() => onDownload(it)} className="text-xs bg-gray-100 hover:bg-gray-200 py-2 rounded-md flex items-center justify-center gap-1"><Download size={12}/> PDF</button>
          <button onClick={() => onEmail(it)} className="text-xs bg-gray-100 hover:bg-gray-200 py-2 rounded-md flex items-center justify-center gap-1"><Mail size={12}/> Email</button>
          <button onClick={() => onWhatsApp(it)} className="text-xs bg-[#25D366] text-white py-2 rounded-md flex items-center justify-center gap-1"><MessageCircle size={12}/> WhatsApp</button>
        </div>
      </div>
    </div>
  );
};

export default VoucherPreviewModal;
